/**
 * JobPipeline.jsx — 12-Step guided job workflow (MUI)
 *
 * Features:
 *  - Steps marked done via DB timestamps + status checks
 *  - "Next step" highlight (first undone, non-disabled)
 *  - Contextual descriptions (dynamic amounts, crew count)
 *  - Records timestamps on action fire (crewNotifiedAt, etc.)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Grid, LinearProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, TextField, InputAdornment, Collapse } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fmtC, fmtD, capitalize } from '../../lib/utils';
import { useToast } from '../layout/Toast';
import { isValidTransition } from '../../lib/statusTransitions';
import { openEmail } from '../../lib/emailTemplates';
import { useQuotes } from '../../hooks/useQuotes';
import { useAuth } from '../../hooks/useAuth';
import { normalizeSnapshot, getAllEarnings, deptLeads } from '../../lib/calcEngine';
import { downloadJobICS } from '../../lib/icsUtils';
import { useGcalConfig, useSyncToGcal, useGcalStatus } from '../../hooks/useGcalSync';
import { useXeroStatus, useSyncInvoiceToXero } from '../../hooks/useXero';
import DeliveryModal from './DeliveryModal';
import QuickInvoiceModal from '../invoices/QuickInvoiceModal';
import SendQuoteModal from '../quotes/SendQuoteModal';
import { PAYMENT_METHODS, DEFAULT_PAYMENT_METHOD, notePlaceholder } from '../../lib/paymentMethods';

const CONFIRM_METHODS = [
  { value: 'email', label: '✉ Email', default: true },
  { value: 'message', label: '💬 Text / Message' },
  { value: 'phone', label: '📞 Phone Call' },
  { value: 'in-person', label: '🤝 In Person' },
  { value: 'deposit', label: '💰 Paid Deposit' },
];

export default function JobPipeline({ job, settings, invoices = [], roster = [], onCreateInvoice, onStatusChange, onUpdateJob, onRecordPayment, onSendInvoice }) {
  const { profile } = useAuth();
  const senderName = profile?.full_name || '';
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: gcalConfig } = useGcalConfig();
  const syncToGcal = useSyncToGcal();
  const { data: gcalStatus } = useGcalStatus(job?.id);
  const gcalEnabled = gcalConfig?.configured === true;
  const { data: xeroStatus } = useXeroStatus();
  const xeroSyncMutation = useSyncInvoiceToXero();
  const xeroConnected = xeroStatus?.connected === true;
  const { data: allQuotes = [] } = useQuotes();

  // Latest quote linked to this job (non-superseded, non-rejected) — used for the
  // Send Quote pre-flight modal in step 2.
  const linkedQuote = (() => {
    if (!job?.id) return null;
    const candidates = allQuotes
      .filter((q) => q.jobId === job.id && q.status !== 'superseded' && q.status !== 'rejected')
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return candidates[0] || null;
  })();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMethod, setConfirmMethod] = useState('email');
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryLink, setDeliveryLink] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [quickInvType, setQuickInvType] = useState(null); // 'deposit' | 'balance' | 'full' | null
  // Record Payment form state (inline expand-in-place within the Record Payment Dialog)
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState(DEFAULT_PAYMENT_METHOD);
  const [payNote, setPayNote] = useState('');
  const [paySaving, setPaySaving] = useState(false);
  // SendQuoteModal: open when the user clicks step 2 "Send Quote".
  const [sendQuoteOpen, setSendQuoteOpen] = useState(false);
  if (!job) return null;

  // ── Derived state ──────────────────────────────────────────
  const hasQuote = !!job.calcSnapshot?.quoteNumber || job.status !== 'intake';
  const isQuoted = ['quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced', 'paid'].includes(job.status);
  // Staycation workflow: pure post-production (no shoots) unless a shoot date
  // is explicitly set. Drives step relabels + hiding the shoot-centric steps.
  const isStaycation = job.workflowType === 'staycation';
  const hasShootDate = !!(job.shootDate || (Array.isArray(job.shootDates) && job.shootDates.length));
  const hideShootSteps = isStaycation && !hasShootDate;
  const hasDeposit = invoices.some((inv) => inv.invoiceType === 'deposit' && inv.status !== 'void');
  const ns = normalizeSnapshot(job.calcSnapshot);
  const hasCalcSnapshot = !!(ns.savedAt || ns.originators.length > 0 || deptLeads(ns.prod).length || deptLeads(ns.post).length);
  const isConfirmed = ['confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced', 'paid'].includes(job.status);
  const isComplete = ['delivered', 'invoiced', 'paid'].includes(job.status);
  const isPartialOrComplete = ['partial_delivery', 'delivered', 'invoiced', 'paid'].includes(job.status);
  const deliveryList = Array.isArray(job.deliveryStatus) ? job.deliveryStatus : [];
  const deliverySentCount = deliveryList.filter((d) => d.sent).length;
  const deliveryTotalCount = deliveryList.length;
  const allDeliverablesSent = deliveryTotalCount > 0 && deliverySentCount === deliveryTotalCount;
  const depositInv = invoices.find((i) => i.invoiceType === 'deposit' && i.status !== 'void');
  const depositSent = hasDeposit && !!(depositInv.sentAt || ['sent', 'partial', 'paid'].includes(depositInv.status));
  const balanceInv = invoices.find((inv) => inv.invoiceType === 'balance' && inv.status !== 'void');
  const hasBalance = !!balanceInv;
  const balanceSent = hasBalance && !!(balanceInv.sentAt || ['sent', 'partial', 'paid'].includes(balanceInv.status));
  // "Full invoice" fallback: when no deposit was ever created, the post-shoot
  // invoice is a FULL invoice — not a balance. Step 10 (below) flips its
  // label/type between "Balance" and "Full" based on whether a deposit exists.
  const fullInv = invoices.find((inv) => inv.invoiceType === 'full' && inv.status !== 'void');
  const hasFull = !!fullInv;
  // finalInv = the invoice step 10 should send/resend (balance if deposit exists, else full)
  const finalKind = hasDeposit ? 'balance' : 'full';
  // Staycation bills as two milestones (halfway + final); relabel deposit/balance
  // accordingly. Underlying invoice_type stays deposit/balance/full.
  const finalKindLabel = isStaycation ? 'Milestone 2 (Final)' : (hasDeposit ? 'Balance' : 'Full');
  const depositLabel = isStaycation ? 'Milestone 1 (Halfway)' : 'Deposit Invoice';
  const finalInv = hasDeposit ? balanceInv : (fullInv || balanceInv);
  const hasFinalInv = !!finalInv;
  const finalSent = hasFinalInv && !!(finalInv.sentAt || ['sent', 'partial', 'paid'].includes(finalInv.status));
  const isPaid = job.status === 'paid';
  const hasPaidInvoice = invoices.some((inv) => inv.status === 'paid');
  const activeInvoices = invoices.filter((inv) => inv.status !== 'void');
  const totalPaid = activeInvoices.reduce((s, inv) => s + (parseFloat(inv.paidAmount) || 0), 0);
  const depositPct = parseFloat(settings?.depositPct) || 50;
  const jobRev = parseFloat(job.revenue) || 0;
  // Balance = revenue minus paid deposit only. Unpaid deposit = balance is full revenue.
  const paidDepositAmt = (depositInv && depositInv.status === 'paid') ? (parseFloat(depositInv.amount) || 0) : 0;
  const balanceAmt = Math.max(0, jobRev - paidDepositAmt);
  // 'extra' invoices are on-top charges, not project coverage — exclude them so
  // an extra charge can't make the balance step read "fully billed" prematurely.
  const coverageInvoiced = activeInvoices.reduce((s, inv) => s + (inv.invoiceType === 'extra' ? 0 : (parseFloat(inv.amount) || 0)), 0);
  const fullyBilled = coverageInvoiced >= jobRev && jobRev > 0;


  // Crew info
  let crewCount = 0;
  let crewNames = '';
  if (hasCalcSnapshot) {
    const names = new Set();
    ns.originators.forEach((o) => names.add(o.name));
    deptLeads(ns.prod).forEach((l) => names.add(l.name));
    ns.prod.hourly.forEach((h) => names.add(h.name));
    deptLeads(ns.post).forEach((l) => names.add(l.name));
    ns.post.hourly.forEach((h) => names.add(h.name));
    crewCount = names.size;
    crewNames = [...names].join(', ');
  }

  // Helper: record a timestamp on the job
  const recordTimestamp = (field) => {
    if (onUpdateJob && !job[field]) {
      onUpdateJob({ id: job.id, [field]: new Date().toISOString() })
        .catch((err) => toast?.(`Failed to save ${field}: ${err.message}`, 'error'));
    }
  };

  // ── Step definitions ───────────────────────────────────────
  const allSteps = [
    {
      num: 1, icon: '💲', title: hasQuote ? 'Update Quote' : 'Create Quote',
      desc: hasQuote ? `Revise quote (${fmtC(jobRev)})` : (jobRev ? `Quote: ${fmtC(jobRev)}` : 'Build a quote in the Quote Maker'),
      done: false, enabled: true, targetStatus: hasQuote ? null : 'quoted',
      action: () => navigate(`/quotes?jobId=${job.id}`),
    },
    {
      num: 2, icon: '✉', title: hasQuote && /-R\d+$/.test(job.calcSnapshot?.quoteNumber || '') ? 'Resend Revised Quote' : 'Send Quote',
      desc: job.contactEmail
        ? (linkedQuote
            ? `Email quote to ${job.contactName || 'client'}`
            : 'No linked quote found yet')
        : 'Add client email first',
      done: isQuoted, enabled: hasQuote && !!job.contactEmail && !!linkedQuote, targetStatus: 'quoted',
      action: () => {
        if (!linkedQuote) {
          // Defensive fallback: pipeline thought there's a quote (calcSnapshot.quoteNumber)
          // but no quotes row exists. Fall back to the legacy mailto-only flow so the user
          // can still get an email out, then auto-transition the job.
          openEmail('quote_sent', { toEmail: job.contactEmail, job, settings, senderName });
          if (isValidTransition(job.status, 'quoted')) onStatusChange?.('quoted');
          return;
        }
        setSendQuoteOpen(true);
      },
    },
    {
      num: 3, icon: '🧾',
      title: depositSent ? `Resend ${depositLabel}` : (hasDeposit ? `Send ${depositLabel}` : `Create ${depositLabel}`),
      desc: depositSent
        ? `${isStaycation ? 'Milestone 1' : 'Deposit'} sent (${fmtC(depositInv?.amount || 0)}) — click to resend`
        : hasDeposit
          ? `${isStaycation ? 'Milestone 1' : 'Deposit'} created (${fmtC(depositInv?.amount || 0)}) — click to send`
          : `${depositPct}% ${isStaycation ? 'halfway payment' : 'deposit'} · ${fmtC(jobRev * (depositPct / 100))}`,
      done: depositSent, enabled: isQuoted, targetStatus: null,
      action: () => {
        if (hasDeposit) {
          onSendInvoice?.(depositInv);
          return;
        }
        setQuickInvType('deposit');
      },
    },
    {
      num: 4, icon: '⚡', title: isStaycation ? 'Run Staycation Split' : 'Run Pay Split',
      desc: hasCalcSnapshot
        ? `${crewCount} crew assigned`
        : (jobRev ? `Split ${fmtC(jobRev)} ${isStaycation ? 'across segments' : 'across team'}` : 'Add revenue first'),
      done: hasCalcSnapshot, enabled: true, targetStatus: null,
      action: () => navigate(`/calculator?jobId=${job.id}`),
    },
    {
      num: 5, icon: '👥', title: 'Notify Crew',
      desc: hasCalcSnapshot ? `${crewCount} crew assigned` : 'Run pay split to assign crew first',
      done: !!job.crewNotifiedAt, enabled: hasCalcSnapshot, targetStatus: null,
      action: () => {
        // Build crew email list from calcSnapshot + roster
        const crewSet = new Map();
        ns.originators.forEach((o) => { if (o.rosterId) crewSet.set(o.rosterId, o.name); });
        deptLeads(ns.prod).forEach((l) => { if (l.rosterId) crewSet.set(l.rosterId, l.name); });
        ns.prod.hourly?.forEach((h) => { if (h.rosterId) crewSet.set(h.rosterId, h.name); });
        deptLeads(ns.post).forEach((l) => { if (l.rosterId) crewSet.set(l.rosterId, l.name); });
        ns.post.hourly?.forEach((h) => { if (h.rosterId) crewSet.set(h.rosterId, h.name); });
        const crewEmails = Array.from(crewSet.entries())
          .map(([rid, name]) => {
            const member = roster.find((r) => r.id === rid);
            return member?.email || '';
          })
          .filter(Boolean);
        const toField = crewEmails.join(',');
        openEmail('crew_notification', { toEmail: toField, job, settings, roster, senderName });
        recordTimestamp('crewNotifiedAt');
      },
    },
    {
      num: 6, icon: '📅', title: gcalEnabled ? 'Sync to Google Calendar' : 'Send Calendar Invites',
      desc: job.shootDate
        ? (hasCalcSnapshot
          ? (gcalEnabled
            ? (gcalStatus?.synced ? `Synced ${gcalStatus.events.length} event${gcalStatus.events.length !== 1 ? 's' : ''} — click to re-sync` : `Push to Google Calendar`)
            : `Download .ics for ${fmtD(job.shootDate)}`)
          : 'Assign crew first via pay split')
        : 'Set shoot date first',
      done: !!job.calInvitesSentAt, enabled: !!job.shootDate && hasCalcSnapshot, targetStatus: null,
      applies: !hideShootSteps,
      action: () => {
        if (gcalEnabled) {
          // Sync to Google Calendar via API
          syncToGcal.mutate({ jobId: job.id }, {
            onSuccess: (data) => {
              const parts = [];
              if (data.created) parts.push(`${data.created} created`);
              if (data.updated) parts.push(`${data.updated} updated`);
              if (data.deleted) parts.push(`${data.deleted} removed`);
              toast(parts.length ? `Calendar synced: ${parts.join(', ')}` : 'Calendar up to date', 'success');
              recordTimestamp('calInvitesSentAt');
            },
            onError: (err) => toast(`Calendar sync failed: ${err.message}`, 'error'),
          });
        } else {
          // Fallback: ICS file download
          const crewSet = new Map();
          ns.originators.forEach((o) => { if (o.rosterId) crewSet.set(o.rosterId, o.name); });
          deptLeads(ns.prod).forEach((l) => { if (l.rosterId) crewSet.set(l.rosterId, l.name); });
          ns.prod.hourly?.forEach((h) => { if (h.rosterId) crewSet.set(h.rosterId, h.name); });
          deptLeads(ns.post).forEach((l) => { if (l.rosterId) crewSet.set(l.rosterId, l.name); });
          ns.post.hourly?.forEach((h) => { if (h.rosterId) crewSet.set(h.rosterId, h.name); });
          const attendees = Array.from(crewSet.entries()).map(([rid, name]) => {
            const member = roster.find((r) => r.id === rid);
            return { name, email: member?.email || '' };
          }).filter((a) => a.email);
          const dlLines = ns.deliverableLines || [];
          const shootTimesMap = {};
          const shootDurMap = {};
          dlLines.forEach((dl) => {
            if (dl.shootDate && dl.shootTime) {
              if (!shootTimesMap[dl.shootDate] || dl.shootTime < shootTimesMap[dl.shootDate]) {
                shootTimesMap[dl.shootDate] = dl.shootTime;
              }
            }
            if (dl.shootDate && dl.dur) {
              shootDurMap[dl.shootDate] = (shootDurMap[dl.shootDate] || 0) + dl.dur;
            }
          });
          downloadJobICS(job, { attendees, shootTimesMap, shootDurMap });
          recordTimestamp('calInvitesSentAt');
        }
      },
    },
    {
      num: 7, icon: '✅', title: 'Confirm Booking',
      desc: isConfirmed ? `Confirmed${job.confirmedMethod ? ` via ${job.confirmedMethod}` : ''}` : 'Client confirms — mark as confirmed',
      done: isConfirmed, enabled: isQuoted && !isConfirmed, targetStatus: 'confirmed',
      action: () => { setConfirmMethod('email'); setConfirmOpen(true); },
      // Mistake escape hatch: while still at 'confirmed', the other half of the
      // button moves the job back to Quoted (clears the confirmed-via stamp and
      // flips the calendar events back to [SOFT] via the normal status flow).
      // Hidden once a deposit is paid — the auto-status engine treats a paid
      // deposit as confirmation and would immediately re-advance the job.
      secondary: job.status === 'confirmed' && !invoices.some((inv) => inv.invoiceType === 'deposit' && inv.status === 'paid') ? {
        label: 'Unconfirm',
        title: 'Made a mistake? Move the job back to Quoted',
        action: () => {
          if (window.confirm('Unconfirm this booking and move the job back to Quoted?')) onStatusChange?.('quoted');
        },
      } : null,
    },
    {
      num: 8, icon: '📋', title: 'Send Shoot Details',
      desc: job.contactEmail && job.shootDate ? `Send location & schedule to ${job.contactName || 'client'}` : 'Need email & shoot date first',
      done: !!job.shootDetailsSentAt, enabled: isConfirmed && !!job.contactEmail, targetStatus: null,
      applies: !hideShootSteps,
      action: () => {
        openEmail('booking_confirmed', { toEmail: job.contactEmail, job, settings, senderName });
        recordTimestamp('shootDetailsSentAt');
      },
    },
    {
      num: 9, icon: '📦', title: 'Send Delivery',
      desc: (() => {
        if (allDeliverablesSent) return `All ${deliveryTotalCount} deliverables sent`;
        if (isComplete && !deliveryTotalCount) return 'All deliverables sent';
        if (isPartialOrComplete && deliveryTotalCount) return `${deliverySentCount}/${deliveryTotalCount} delivered — click to update`;
        if (isPartialOrComplete) return 'Some deliverables sent — click to update';
        return job.contactEmail ? 'Review deliverables & send links' : 'Add email first';
      })(),
      done: deliveryTotalCount > 0 ? allDeliverablesSent : isComplete, enabled: isConfirmed && !!job.contactEmail, targetStatus: null,
      action: () => setDeliveryOpen(true),
    },
    {
      num: 10, icon: '🧾',
      title: finalSent
        ? `Resend ${finalKindLabel} Invoice`
        : (hasFinalInv ? `Send ${finalKindLabel} Invoice` : `Create ${finalKindLabel} Invoice`),
      desc: finalSent
        ? `${finalKindLabel} sent (${fmtC(finalInv?.amount || 0)}) — click to resend`
        : hasFinalInv
          ? `${finalKindLabel} created (${fmtC(finalInv?.amount || 0)}) — click to send`
          : fullyBilled ? 'Fully billed — no balance due'
            : (balanceAmt > 0 ? `Remaining: ${fmtC(balanceAmt)}` : 'Fully paid — no balance due'),
      done: finalSent,
      enabled: hasFinalInv || ((isComplete || hasDeposit) && !fullyBilled),
      targetStatus: null,
      action: () => {
        if (hasFinalInv) {
          // Send or resend — open email + persist sentAt
          onSendInvoice?.(finalInv);
          return;
        }
        // No final invoice yet — open quick-create modal with right type
        setQuickInvType(finalKind);
      },
    },
    {
      num: 11, icon: '💰', title: 'Record Payment',
      desc: activeInvoices.length ? 'Mark invoices as paid' : 'Create invoice first',
      done: isPaid || hasPaidInvoice, enabled: hasDeposit || hasBalance, targetStatus: 'paid',
      action: () => setPaymentOpen(true),
    },
  ];

  // Hide steps that don't apply to this workflow (e.g. shoot-centric steps for
  // pure-post staycation). Filtering before nextStep/progress/render keeps the
  // index, completed count, and denominator consistent.
  const steps = allSteps.filter((s) => s.applies !== false);

  // ── Find "next step" ───────────────────────────────────────
  const nextStepIdx = steps.findIndex((s) => !s.done && s.enabled);
  const completedCount = steps.filter((s) => s.done).length;
  const progress = (completedCount / steps.length) * 100;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="overline">Job Pipeline</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{completedCount}/{steps.length} complete</Typography>
      </Box>

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 2, borderRadius: 1, height: 6 }} />

      <Grid container spacing={1}>
        {steps.map((step, idx) => {
          const isNext = idx === nextStepIdx;
          const willAdvance = !step.done && step.targetStatus && isValidTransition(job.status, step.targetStatus);
          const isClickable = step.done || step.enabled || willAdvance;
          const mainButton = (
              <Button
                fullWidth={!step.secondary}
                data-track={`pipeline:${step.title}`}
                onClick={step.action}
                disabled={!isClickable}
                variant={isNext ? 'contained' : 'outlined'}
                color={isNext ? 'primary' : undefined}
                title={willAdvance ? `Will move job to "${capitalize(step.targetStatus)}"` : undefined}
                sx={{
                  ...(step.secondary ? { flex: 1, minWidth: 0 } : {}),
                  justifyContent: 'flex-start', textTransform: 'none', py: 1, px: 1.5, gap: 1,
                  ...(!step.done && !isNext && {
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    color: 'text.primary',
                    opacity: isClickable ? 1 : 0.2,
                    '&:hover': isClickable ? { borderColor: 'text.disabled' } : {},
                  }),
                  ...(step.done && { borderColor: 'success.main', color: 'text.primary' }),
                  ...(isNext && !step.done ? { boxShadow: 2 } : {}),
                  ...(willAdvance && !isNext ? { borderStyle: 'dashed' } : {}),
                }}
              >
                {step.done ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                ) : (
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: isNext ? 'primary.contrastText' : 'action.hover',
                    color: isNext ? 'primary.main' : 'text.secondary',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {step.num}
                  </Box>
                )}
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}>
                    <span>{step.icon}</span> {step.title}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.7, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'inherit' }}>
                    {step.desc}
                    {willAdvance && <span style={{ marginLeft: 4, fontWeight: 600 }}>→ {capitalize(step.targetStatus)}</span>}
                  </Typography>
                </Box>
              </Button>
          );
          return (
            <Grid key={step.num} size={{ xs: 12, md: 6 }}>
              {step.secondary ? (
                <Box sx={{ display: 'flex', gap: 0.5, height: '100%' }}>
                  {mainButton}
                  <Button
                    variant="outlined"
                    onClick={step.secondary.action}
                    title={step.secondary.title}
                    sx={{
                      textTransform: 'none', minWidth: 0, px: 1.25, fontSize: '0.65rem', flexShrink: 0,
                      borderColor: 'divider', color: 'text.secondary',
                      '&:hover': { borderColor: 'warning.main', color: 'warning.main', bgcolor: 'transparent' },
                    }}
                  >
                    ↩ {step.secondary.label}
                  </Button>
                </Box>
              ) : mainButton}
            </Grid>
          );
        })}
      </Grid>

      {/* Delivery Modal */}
      <DeliveryModal
        open={deliveryOpen}
        onClose={() => setDeliveryOpen(false)}
        job={job}
        settings={settings}
        onUpdateJob={onUpdateJob}
        onStatusChange={onStatusChange}
      />

      {/* Quick Invoice Modal (steps 3 & 11) */}
      <QuickInvoiceModal
        open={!!quickInvType}
        onClose={() => setQuickInvType(null)}
        type={quickInvType}
        job={job}
        settings={settings}
        existingInvoices={invoices}
        onCreatedAndSend={async (inv) => {
          // Parent handles Xero sync + mailto + sentAt + onRefresh
          await onSendInvoice?.(inv);
        }}
      />

      {/* Send Quote Modal (step 2) */}
      {sendQuoteOpen && linkedQuote && (
        <SendQuoteModal
          quote={linkedQuote}
          job={job}
          onClose={() => setSendQuoteOpen(false)}
          onSent={() => {
            // Mirror the old step-2 behavior: move the job into 'quoted' status
            // if the transition is valid. quoteSync will keep things aligned
            // server-side, but doing it here too keeps the UI snappy.
            if (isValidTransition(job.status, 'quoted')) onStatusChange?.('quoted');
          }}
        />
      )}

      {/* Record Payment Modal */}
      <Dialog
        open={paymentOpen}
        onClose={() => { setPaymentOpen(false); setExpandedInvoiceId(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Record Payment</DialogTitle>
        <DialogContent>
          {(() => {
            const unpaid = activeInvoices.filter((inv) => inv.status !== 'paid');
            if (!unpaid.length) return <Typography variant="body2" color="text.secondary">All invoices are paid.</Typography>;
            return unpaid.map((inv) => {
              const remaining = Math.max(0, (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0));
              const isExpanded = expandedInvoiceId === inv.id;
              const expand = () => {
                setExpandedInvoiceId(inv.id);
                setPayAmount(String(remaining.toFixed(2)));
                setPayMethod(DEFAULT_PAYMENT_METHOD);
                setPayNote('');
              };
              const collapse = () => {
                setExpandedInvoiceId(null);
                setPayAmount(''); setPayNote(''); setPayMethod(DEFAULT_PAYMENT_METHOD);
              };
              const submit = async () => {
                const amt = parseFloat(payAmount);
                if (!amt || amt <= 0) { toast('Enter a valid amount', 'error'); return; }
                setPaySaving(true);
                try {
                  await onRecordPayment?.(inv.id, { amount: amt, method: payMethod, note: payNote.trim() });
                  // Collapse this row; keep dialog open for more payments
                  collapse();
                  // If no more unpaid, close dialog
                  const stillUnpaid = activeInvoices.filter((i) => i.id !== inv.id && i.status !== 'paid').length;
                  // Note: activeInvoices snapshot may be stale; rely on refresh to re-evaluate.
                  // We still close if this invoice fully paid AND no others remained unpaid on this render.
                  if (amt >= remaining && stillUnpaid === 0) setPaymentOpen(false);
                } catch (err) {
                  toast(`Failed: ${err?.message || 'unknown error'}`, 'error');
                } finally {
                  setPaySaving(false);
                }
              };
              return (
                <Box key={inv.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {inv.invoiceType === 'deposit' ? 'Deposit' : 'Balance'} Invoice
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtC(inv.amount)}
                        {(parseFloat(inv.paidAmount) || 0) > 0 && (
                          <> · Paid {fmtC(inv.paidAmount)} · Remaining {fmtC(remaining)}</>
                        )}
                      </Typography>
                    </Box>
                    {!isExpanded && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={expand}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >Mark Paid</Button>
                    )}
                  </Box>

                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        inputProps={{ min: 0, max: remaining, step: '0.01' }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        helperText={`Max remaining: ${fmtC(remaining)}`}
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Payment Method</Typography>
                        <RadioGroup
                          row
                          value={payMethod}
                          onChange={(e) => setPayMethod(e.target.value)}
                          sx={{ mt: 0.5 }}
                        >
                          {PAYMENT_METHODS.map((m) => (
                            <FormControlLabel
                              key={m.value}
                              value={m.value}
                              control={<Radio size="small" />}
                              label={<span style={{ fontSize: '0.8rem' }}>{m.icon} {m.label}</span>}
                              sx={{ mr: 1.5 }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>
                      <TextField
                        label="Reference / Note"
                        size="small"
                        value={payNote}
                        onChange={(e) => setPayNote(e.target.value)}
                        placeholder={notePlaceholder(payMethod)}
                      />
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={collapse} disabled={paySaving}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={submit} disabled={paySaving}>
                          {paySaving ? 'Recording…' : 'Record'}
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            });
          })()}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => { setPaymentOpen(false); setExpandedInvoiceId(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Booking Modal */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700 }}>Confirm Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>How did the client confirm?</Typography>
          <RadioGroup value={confirmMethod} onChange={(e) => setConfirmMethod(e.target.value)}>
            {CONFIRM_METHODS.map((m) => (
              <FormControlLabel key={m.value} value={m.value} control={<Radio size="small" />} label={m.label} sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }} />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button size="small" variant="contained" onClick={() => {
            onUpdateJob?.({ id: job.id, confirmedAt: new Date().toISOString(), confirmedMethod: confirmMethod });
            if (isValidTransition(job.status, 'confirmed')) onStatusChange?.('confirmed');
            if (confirmMethod === 'email' && job.contactEmail) {
              openEmail('booking_confirmed', { toEmail: job.contactEmail, job, settings, senderName });
            }
            setConfirmOpen(false);
          }}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
